"use client";

// Botón "Continuar con Google".
// Usa el flujo de callback (redirección) porque funciona en todos los navegadores,
// incluidos los que bloquean cookies de terceros. Si el servidor solo tiene el
// Client ID (sin secreto), cae al botón de Google Identity Services.
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { api, setTokens } from "../lib/api";

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  roles: string[];
}
interface Config {
  habilitado: boolean;
  clientId: string | null;
  callback: boolean;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void;
          renderButton: (el: HTMLElement, options: object) => void;
        };
      };
    };
  }
}

// El destino llega desde la pantalla que monta el botón (?next de la URL), o sea
// que es texto que escribe cualquiera: se revalida acá igual que en el login para
// que nadie use el "continuar con Google" como trampolín a otro dominio.
function destinoValido(next: string): string {
  if (!next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "";
  return next;
}

// next vacío = "no pidieron destino": ahí manda la regla de siempre (el admin
// arranca en su panel y el resto en su perfil).
export default function GoogleButton({ next = "" }: { next?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [cfg, setCfg] = useState<Config | null>(null);
  const [error, setError] = useState("");
  const destino = destinoValido(next);

  useEffect(() => {
    api<Config>("/auth/google/config")
      .then(setCfg)
      .catch(() => setCfg({ habilitado: false, clientId: null, callback: false }));
  }, []);

  // Modo respaldo: botón de Google Identity Services (solo necesita el Client ID)
  useEffect(() => {
    if (!cfg?.habilitado || cfg.callback || !cfg.clientId || !ref.current) return;

    const init = () => {
      if (!window.google || !ref.current) return;
      window.google.accounts.id.initialize({
        client_id: cfg.clientId,
        callback: async (resp: { credential: string }) => {
          try {
            const r = await api<AuthResponse>("/auth/google", {
              method: "POST",
              body: JSON.stringify({ credential: resp.credential }),
            });
            setTokens(r.accessToken, r.refreshToken);
            // replace: volver atrás desde el destino no debe recaer en el login
            router.replace(destino || (r.roles.includes("ADMIN") ? "/admin" : "/perfil"));
          } catch (e) {
            setError(e instanceof Error ? e.message : "Error con Google");
          }
        },
      });
      window.google.accounts.id.renderButton(ref.current, {
        theme: "outline", size: "large", text: "continue_with", locale: "es", width: 320,
      });
    };

    if (window.google?.accounts) init();
    else {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.onload = init;
      document.head.appendChild(s);
    }
  }, [cfg, destino, router]);

  if (!cfg?.habilitado) return null;

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-3 w-full text-[#808D88] text-xs">
        <div className="h-px bg-white/15 flex-1" /> o <div className="h-px bg-white/15 flex-1" />
      </div>

      {cfg.callback ? (
        // Sigue siendo <a> y no <Link>: /api/... es una redirección del servidor
        // hacia Google, no una ruta de Next que se pueda navegar del lado del cliente.
        <a
          href={`/api/auth/google/start?next=${encodeURIComponent(destino || "/perfil")}`}
          className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/5 py-3.5 font-semibold hover:bg-white/10 transition"
        >
          <GoogleLogo /> Continuar con Google
        </a>
      ) : (
        <div ref={ref} />
      )}

      {error && <p className="text-sm text-[#DE705C]">{error}</p>}
    </div>
  );
}

// Logo oficial de Google: no está en Lucide (las marcas salieron de la librería)
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-3.9H24v7.1h12c-.2 1.8-1.5 4.6-4.4 6.4l6.7 5.2C42.2 35.1 45 30 45 24z" />
      <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.9-5.4c-1.8 1.3-4.3 2.2-7.6 2.2-5.8 0-10.7-3.8-12.5-9.1l-7.1 5.5C8 41.2 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.5 28.4c-.5-1.4-.7-2.9-.7-4.4s.3-3 .7-4.4l-7.1-5.5C2.9 17 2 20.4 2 24s.9 7 2.4 9.9l7.1-5.5z" />
      <path fill="#EA4335" d="M24 10.7c4.1 0 6.9 1.8 8.5 3.3l6.2-6C34.9 4.4 29.9 2 24 2 15.4 2 8 6.8 4.4 14.1l7.1 5.5C13.3 14.4 18.2 10.7 24 10.7z" />
    </svg>
  );
}
